/**
 * @fileoverview Test Session API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Test Session module.
 * Single source of truth for all test session-related API definitions.
 *
 * This contract defines the wizard flow for the Ulrich LearnType test:
 * - Session creation and management
 * - Question answering
 * - Session completion
 * - Progress tracking
 */

import { z } from 'zod';
import { createContract, extractRoutes, ApiSuccessResponseSchema, ApiPaginatedResponseSchema } from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Test Session Status Enum
 */
export enum TestSessionStatus {
	CREATED = 'created',
	IN_PROGRESS = 'in_progress',
	PAUSED = 'paused',
	COMPLETED = 'completed',
	ABANDONED = 'abandoned',
	EXPIRED = 'expired',
}

/**
 * Test Session Type Enum
 */
export enum TestSessionType {
	LEARNING_PROFILE = 'learning_profile',
	PERSONALITY = 'personality',
	COGNITIVE = 'cognitive',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * NOTE: userId is NOT included here because it should be accessed through
 * the session relation in the database (testSession.session.userId).
 * The session_tracking table is the single source of truth for userId.
 */
export const TestSessionSchema = z.object({
	id: z.uuid(),
	sessionId: z.string(),
	// ❌ userId removed - access through session relation
	testType: z.enum(TestSessionType),
	status: z.enum(TestSessionStatus),
	progress: z.number().min(0).max(100),
	answers: z.record(z.string(), z.unknown()).optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
	createdAt: z.iso.datetime(), // ISO 8601 datetime
	updatedAt: z.iso.datetime(), // ISO 8601 datetime
	completedAt: z.iso.datetime().nullable(), // ISO 8601 datetime
});
/**
 * Create Test Session Request Schema
 * Includes complete basic info and onboarding context from onboarding wizard
 * All data is stored in metadata for later TestResult creation
 */
export const CreateTestSessionSchema = z.object({
	testType: z.enum(TestSessionType).default(TestSessionType.LEARNING_PROFILE),

	// ✅ EXTENDED: All basic info fields from onboarding
	basicInfo: z.object({
		childName: z.string().min(1, 'Child name is required'),
		childAge: z.number().int().positive('Child age must be positive'),
		childSchool: z.string().optional().default(''),
		childSituation: z.string().optional().default(''),
		childInterests: z.string().optional(), // ← NEW
		childGender: z.enum(['male', 'female', 'other']).optional(), // ← NEW
	}).optional(),

	// Onboarding context (challenges, goals, parent feeling)
	onboardingContext: z.object({
		challenges: z.array(z.string()).optional(),
		parentFeeling: z.string().optional(),
		selectedGoals: z.array(z.string()).optional(),
		startedAt: z.iso.datetime().optional(),
		completedAt: z.iso.datetime().optional(),
	}).optional(),

	customerEmail: z.email().optional(), // Optional email for early capture
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateTestSessionPayload = z.infer<typeof CreateTestSessionSchema>;

/**
 * Test Session Response Schema
 *
 * NOTE: userId and email are optional virtual properties populated from the
 * session relation (testSession.session.userId and testSession.session.user.email).
 * They are included for convenience in admin responses.
 */
export const TestSessionResponseSchema = z.object({
	id: z.uuid(),
	sessionId: z.string(),
	userId: z.string().uuid().nullable().optional(), // Virtual property from session.user.id
	email: z.string().email().nullable().optional(), // Virtual property from session.user.email
	testType: z.enum(TestSessionType),
	status: z.enum(TestSessionStatus),
	progress: z.number().min(0).max(100),
	answers: z.record(z.string(), z.unknown()).optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
	createdAt: z.iso.datetime(), // ISO 8601 datetime
	updatedAt: z.iso.datetime(), // ISO 8601 datetime
	completedAt: z.iso.datetime().nullable(), // ISO 8601 datetime
});
export type TestSessionResponse = z.infer<typeof TestSessionResponseSchema>;

/**
 * Update Test Session Request Schema
 */
export const UpdateTestSessionSchema = z.object({
	status: z.enum(TestSessionStatus).optional(),
	answers: z.record(z.string(), z.unknown()).optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type UpdateTestSessionPayload = z.infer<typeof UpdateTestSessionSchema>;

/**
 * Answer Question Request Schema
 */
export const AnswerQuestionSchema = z.object({
	questionId: z.string().min(1, 'Question ID is required'),
	answer: z.unknown(), // Can be any type (number, string, array, etc.)
	currentStep: z.string().optional(),
	progress: z.number().min(0).max(100).optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type AnswerQuestionPayload = z.infer<typeof AnswerQuestionSchema>;

/**
 * Complete Test Session Request Schema
 * Note: Only premium analysis is available (no basic tier)
 *
 * Basic info and onboarding context should already be in TestSession.metadata from session creation
 * This schema allows optional inclusion of basicInfo and onboarding context if needed for completion
 * (e.g., when session was recreated due to deletion/expiration)
 */
export const CompleteTestSessionSchema = z.object({
	scores: z.record(z.string(), z.number()).optional(),
	analysisType: z.literal('premium').optional().default('premium'), // Only premium analysis available
	answers: z.record(z.string(), z.unknown()).optional(), // Optional final answers

	// ✅ NEW: Optional basic info for completion (if not provided in start or session was recreated)
	basicInfo: z.object({
		childName: z.string().min(1, 'Child name is required').optional(),
		childAge: z.number().int().positive('Child age must be positive').optional(),
		childSchool: z.string().optional().default(''),
		childSituation: z.string().optional().default(''),
		childInterests: z.string().optional(),
		childGender: z.enum(['male', 'female', 'other']).optional(),
	}).optional(),

	// ✅ NEW: Optional onboarding context for completion (if not provided in start)
	onboardingContext: z.object({
		challenges: z.array(z.string()).optional(),
		parentFeeling: z.string().optional(),
		selectedGoals: z.array(z.string()).optional(),
		startedAt: z.string().optional(), // ISO timestamp
		completedAt: z.string().optional(), // ISO timestamp
	}).optional(),

	metadata: z.record(z.string(), z.unknown()).optional(), // Optional completion metadata
});
export type CompleteTestSessionPayload = z.infer<typeof CompleteTestSessionSchema>;

/**
 * Complete Test Session Data Response Schema
 * Includes testId generated by backend for payment processing
 */
export const CompleteTestSessionDataSchema = z.object({
	session: TestSessionResponseSchema,
	answers: z.record(z.string(), z.unknown()),
	scores: z.record(z.string(), z.number()).optional(),
	analysisType: z.literal('premium').optional().default('premium'),
	testId: z.string(), // Backend-generated testId for payment
});
export type CompleteTestSessionData = z.infer<typeof CompleteTestSessionDataSchema>;

/**
 * List Test Sessions Query Schema (Admin)
 */
export const ListTestSessionsQuerySchema = z.object({
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(20),
	status: z.enum(TestSessionStatus).optional(),
	testType: z.enum(TestSessionType).optional(),
	userId: z.string().optional(),
	sortBy: z.enum(['createdAt', 'updatedAt', 'completedAt']).default('createdAt'),
	sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type ListTestSessionsQuery = z.infer<typeof ListTestSessionsQuerySchema>;

// ============================================================================
// API RESPONSE WRAPPERS (Consistent Response Format)
// ============================================================================

/**
 * Test Session API Response
 * { success: true, data: TestSessionResponse, timestamp: string }
 */
export const TestSessionApiResponseSchema = ApiSuccessResponseSchema(TestSessionResponseSchema);

/**
 * Complete Test Session Data API Response
 * { success: true, data: CompleteTestSessionData, timestamp: string }
 */
export const CompleteTestSessionDataApiResponseSchema = ApiSuccessResponseSchema(CompleteTestSessionDataSchema);

/**
 * Paginated Test Sessions API Response (Admin)
 * { success: true, data: TestSessionResponse[], meta: PaginationMeta, timestamp: string }
 */
export const PaginatedTestSessionsApiResponseSchema = ApiPaginatedResponseSchema(TestSessionResponseSchema);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Test Session API Contract
 *
 * Defines the complete API surface for the Test Session module.
 * Both frontend and backend use this as the single source of truth.
 */
export const TEST_SESSION_API_CONTRACT = createContract('/test-session', {
	/**
	 * POST /test-session/start
	 * Start a new test session
	 */
	start: {
		method: 'POST' as const,
		path: '/start',
		body: CreateTestSessionSchema,
		response: ApiSuccessResponseSchema(TestSessionResponseSchema),
	},

	/**
	 * GET /test-session/current
	 * Get the current test session for the tracking ID
	 */
	getCurrent: {
		method: 'GET' as const,
		path: '/current',
		response: ApiSuccessResponseSchema(TestSessionResponseSchema),
	},

	/**
	 * POST /test-session/answer
	 * Submit an answer for a question
	 */
	answer: {
		method: 'POST' as const,
		path: '/answer',
		body: AnswerQuestionSchema,
		response: ApiSuccessResponseSchema(TestSessionResponseSchema),
	},

	/**
	 * POST /test-session/complete
	 * Complete the test session and return testId for payment
	 */
	complete: {
		method: 'POST' as const,
		path: '/complete',
		body: CompleteTestSessionSchema,
		response: ApiSuccessResponseSchema(CompleteTestSessionDataSchema),
	},

	/**
	 * GET /test-session/complete-data
	 * Get complete test session data (for analysis)
	 */
	getCompleteData: {
		method: 'GET' as const,
		path: '/complete-data',
		response: ApiSuccessResponseSchema(CompleteTestSessionDataSchema),
	},

	/**
	 * ADMIN ENDPOINTS
	 */

	/**
	 * GET /test-session/admin/list
	 * List all test sessions with pagination and filtering (Admin only)
	 */
	adminList: {
		method: 'GET' as const,
		path: '/admin/list',
		query: ListTestSessionsQuerySchema,
		response: PaginatedTestSessionsApiResponseSchema,
	},

	/**
	 * GET /test-session/admin/:id
	 * Get test session by ID (Admin only)
	 */
	adminGetById: {
		method: 'GET' as const,
		path: '/admin/:id',
		params: z.object({ id: z.string().uuid() }),
		response: ApiSuccessResponseSchema(TestSessionResponseSchema),
	},

	/**
	 * PATCH /test-session/admin/:id
	 * Update test session (Admin only)
	 * Allows manual correction of session data, status, metadata, etc.
	 */
	adminUpdate: {
		method: 'PATCH' as const,
		path: '/admin/:id',
		params: z.object({ id: z.string().uuid() }),
		body: UpdateTestSessionSchema,
		response: ApiSuccessResponseSchema(TestSessionResponseSchema),
	},

	/**
	 * DELETE /test-session/admin/:id
	 * Delete test session (Admin only)
	 * Permanently deletes a test session and related data
	 */
	adminDelete: {
		method: 'DELETE' as const,
		path: '/admin/:id',
		params: z.object({ id: z.string().uuid() }),
		response: ApiSuccessResponseSchema(
			z.object({
				message: z.string(),
				deletedId: z.string().uuid(),
			}),
		),
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const TEST_SESSION_ROUTES = extractRoutes(TEST_SESSION_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type TestSessionApiContract = typeof TEST_SESSION_API_CONTRACT;

/**
 * Export contract for use in API client
 */
export { TEST_SESSION_API_CONTRACT as default };
